/**
 * 16回以上施術理由 新規登録 - Long Term Frequent Create Page
 * Switches between A2 (はり・きゅう) and R2 (マッサージ) forms based on service type.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  AppButton,
  AppCard,
  AppForm,
  AppFormLabel,
  AppSelect,
  AppTextField,
  AppTextArea,
  AppSpin,
  AppRadio,
  AppRadioGroup,
  AppCheckbox,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import {
  useLongTermFrequentCreate,
  useLongTermFrequentMonthData,
  useLongTermFrequentMasterData,
} from '@/hooks/main/useLongTermFrequentEdit';
import useAppToast from '@/hooks/useAppToast';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import {
  longTermFrequentA2Schema,
  longTermFrequentR2Schema,
  type LongTermFrequentA2FormData,
  type LongTermFrequentR2FormData,
} from '@/schemas/longTermFrequentSchema';
import { TENANT_ROUTES } from '@/constants/routes';
import { FORM_MESSAGES } from '@/constants/formMessages';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import {
  A2_DISEASE_NAME_OPTIONS,
  A2_DISEASE_OTHER_ID,
  R2_MASSAGE_PART_OPTIONS,
} from '@/constants/consentForm';
import { UPDATE_ERROR_MESSAGE } from '@/constants/messages';
import { fetchLastVisitDate } from '@/services/longTermFrequentService';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  convGtJDate,
  convGtJMonth,
  warekiMonthToYearmonth,
  yearmonthToDateStr,
} from '@/utils/dateConverter';
import { convertSeirekiWrap } from '@/utils/wareki';
import { resolveR2TreatmentPartLabelsFromStoredConsent } from '@/utils/consentForm';

type FormData = LongTermFrequentA2FormData | LongTermFrequentR2FormData;

const NOTE_TEXT =
  '※傷病名～初療年月日は同意書の内容が反映されます。変更する場合は同意書の修正を行って下さい';

/** RA2NDatePicker wareki → ISO YYYY-MM-DD */
const onWarekiDateChange = (fieldOnChange: (v: string) => void) => (warekiStr: string) => {
  if (!warekiStr?.trim()) {
    fieldOnChange('');
    return;
  }
  const seireki = convertSeirekiWrap(warekiStr);
  const d = dayjs(seireki.replace(/\//g, '-'));
  fieldOnChange(d.isValid() ? d.format('YYYY-MM-DD') : '');
};

/** RA2NDatePicker month wareki → YYYYMM string */
const onWarekiMonthChange = (fieldOnChange: (v: string) => void) => (warekiStr: string) => {
  if (!warekiStr?.trim()) {
    fieldOnChange('');
    return;
  }
  const ym = warekiMonthToYearmonth(warekiStr);
  if (ym) {
    fieldOnChange(String(ym));
  }
};

/**
 * A2: derive 施術の種類 display from treatment_content array.
 * Match the PDF output: only the primary はり/きゅう consent values select this radio.
 * Electrical options such as 電気針/電気温灸/電気光線器 do not decide the main type.
 */
function deriveTreatmentTypeLabel(content: Array<string | number>): string {
  if (!content || content.length === 0) return '';
  const normalized = content.map(v => String(v).trim());
  const hasHari = normalized.some(v => v === 'はり' || v === '1');
  const hasKyu = normalized.some(v => v === 'きゅう' || v === '3');
  if (hasHari && hasKyu) return 'はり・きゅう併用';
  if (hasHari) return 'はり';
  if (hasKyu) return 'きゅう';
  return '';
}

export const LongTermFrequentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant, id: patientId } = useParams<{ tenant?: string; id?: string }>();
  const [searchParams] = useSearchParams();
  const { serviceType: contextServiceType, colors } = useTheme();
  const { tenant: siteType, clinicNumber } = useClinicContext();
  const sidebarOpen = useSidebarOpen();
  const toast = useAppToast();

  const isA2 = (contextServiceType?.toUpperCase() ?? '') === SERVICE_TYPE_A2;
  const isR2 = !isA2;

  const schema = isA2 ? longTermFrequentA2Schema : longTermFrequentR2Schema;

  // HIT-4248: when navigating from 月16回受診者一覧 modal without an existing
  // long_term_frequent record (no 出力 button), the 編集 link includes
  // ?treatment_yearmonth=YYYYMM so 施術年月 is pre-filled instead of empty.
  const presetYearmonth = useMemo(() => {
    const raw = searchParams.get('treatment_yearmonth');
    return raw && /^\d{6}$/.test(raw) ? raw : '';
  }, [searchParams]);

  const defaultValues = useMemo(
    () =>
      isA2
        ? {
            visit_yearmonth: presetYearmonth,
            valuation_date: '',
            degree_of_pain: 5,
            recovery_type_id: null,
            massager_id: null,
            treatment_continuation_reason: '',
            create_document_date: '',
          }
        : {
            visit_yearmonth: presetYearmonth,
            valuation_date: '',
            valuation_betray_type_id: 1,
            valuation_sit_up_type_id: 1,
            valuation_sitting_type_id: 1,
            valuation_stand_up_type_id: 1,
            valuation_standing_type_id: 1,
            recovery_type_id: null,
            massager_id: null,
            treatment_continuation_reason: '',
            create_document_date: '',
          },
    [isA2, presetYearmonth]
  );

  const { control, handleSubmit, watch, setValue, setError } = useTrimmedForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as any,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const currentValues = watch();
  const visitYearmonth = watch('visit_yearmonth');
  const { clearFormChange } = useFormChangeGuard(currentValues, defaultValues);

  const createMutation = useLongTermFrequentCreate(patientId || '');

  // Fetch patient info
  const { data: patient, isLoading: isListLoading } = useMainPatient(
    patientId ? Number(patientId) : undefined
  );

  // Fetch master data
  const { recoveryTypes, valuationTypes } = useLongTermFrequentMasterData(patientId);

  // Fetch all month data in a single call (consent + visits + prev month)
  const { data: monthData } = useLongTermFrequentMonthData(patientId, visitYearmonth);

  const isPrev = monthData?.is_prev ?? false;
  const prevRecoveryId = monthData?.prev_recovery_type_id ?? null;
  // 前月の評価 display: A2 needs both prev existence + recovery_type_id; R2 needs prev existence only.
  const showHasPrev = isA2 ? isPrev && prevRecoveryId != null : isPrev;

  // Prefill from previous month data
  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [hasUserSelectedMassager, setHasUserSelectedMassager] = useState(false);
  useEffect(() => {
    if (monthData && !hasPrefilled && visitYearmonth) {
      // Always sync from month-data (including empty) so switching months clears stale text.
      setValue('treatment_continuation_reason', monthData.prev_treatment_continuation_reason ?? '');
      // 改善状況 is intentionally not copied from the previous month.
      setValue('recovery_type_id', null);
      if (isR2) {
        setValue('valuation_betray_type_id' as any, monthData.prev_valuation_betray_type_id ?? 1);
        setValue('valuation_sit_up_type_id' as any, monthData.prev_valuation_sit_up_type_id ?? 1);
        setValue('valuation_sitting_type_id' as any, monthData.prev_valuation_sitting_type_id ?? 1);
        setValue(
          'valuation_stand_up_type_id' as any,
          monthData.prev_valuation_stand_up_type_id ?? 1
        );
        setValue(
          'valuation_standing_type_id' as any,
          monthData.prev_valuation_standing_type_id ?? 1
        );
      }
      setHasPrefilled(true);
    }
  }, [monthData, hasPrefilled, visitYearmonth, isR2, isPrev, prevRecoveryId, setValue]);

  useEffect(() => {
    setHasPrefilled(false);
    setHasUserSelectedMassager(false);
  }, [visitYearmonth]);

  useEffect(() => {
    if (!monthData || hasUserSelectedMassager) {
      return;
    }

    setValue('massager_id' as any, monthData.default_massager_id ?? null);
  }, [monthData, hasUserSelectedMassager, setValue]);

  const listUrl = TENANT_ROUTES.LONG_TERM_FREQUENTS.LIST(tenant || '', patientId || '');
  const isBusy = createMutation.isPending;

  const handleBack = useCallback(() => {
    if (isBusy) return;
    if (window.confirm(FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION)) {
      clearFormChange();
      authorizeNavigation();
      navigate(listUrl);
    }
  }, [clearFormChange, navigate, listUrl, isBusy]);

  const handleGetLastVisitDate = useCallback(async () => {
    if (!visitYearmonth || !patientId) return;
    try {
      const res = await fetchLastVisitDate(siteType, clinicNumber, patientId, visitYearmonth);
      if (res?.data?.last_visit_date) {
        setValue('create_document_date', res.data.last_visit_date);
      }
    } catch {
      // No visit found - keep current value
    }
  }, [visitYearmonth, patientId, siteType, clinicNumber, setValue]);

  const applyApiFieldErrors = useCallback(
    (error: unknown) => {
      const err = error as {
        response?: { status?: number; data?: { errors?: Record<string, string[]> } };
      };
      if (err.response?.status === 422 && err.response.data?.errors) {
        const apiErrors = err.response.data.errors;
        Object.keys(apiErrors).forEach(key => {
          setError(key as any, {
            type: 'manual',
            message: Array.isArray(apiErrors[key]) ? apiErrors[key][0] : String(apiErrors[key]),
          });
        });
      }
    },
    [setError]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        ...data,
        recovery_type_id:
          data.recovery_type_id != null && data.recovery_type_id > 0
            ? data.recovery_type_id
            : undefined,
      };
      createMutation
        .mutateAsync(payload)
        .then(response => {
          if (response?.success) {
            clearFormChange();
            authorizeNavigation();
            navigate(listUrl);
          }
        })
        .catch((error: unknown) => {
          applyApiFieldErrors(error);
        });
    },
    [createMutation, clearFormChange, navigate, listUrl, applyApiFieldErrors]
  );

  const onInvalid = useCallback(() => {
    toast.error(UPDATE_ERROR_MESSAGE);
  }, [toast]);

  // Derived read-only consent display values
  const checkedDiseases: string[] = Array.isArray(monthData?.disease_name)
    ? monthData.disease_name
    : [];
  const diseaseNameOther = monthData?.disease_name_other ?? '';
  const treatmentTypeLabel = deriveTreatmentTypeLabel(
    Array.isArray(monthData?.treatment_content) ? monthData.treatment_content : []
  );
  const firstTreatmentDateDisplay = monthData?.first_treatment_date
    ? convGtJDate(dayjs(monthData.first_treatment_date))
    : '';
  const visitCountDisplay = monthData?.visit_count ?? 0;
  const massagerOptions = useMemo(
    () => (monthData?.massagers ?? []).map(m => ({ value: m.id, label: m.name })),
    [monthData?.massagers]
  );
  const r2SelectedTreatmentPartLabels = useMemo(
    () =>
      isR2
        ? resolveR2TreatmentPartLabelsFromStoredConsent(monthData?.massage, monthData?.orthomorphia)
        : new Set<string>(),
    [isR2, monthData?.massage, monthData?.orthomorphia]
  );

  if (isListLoading) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  return (
    <div className="patient-edit-page">
      <PatientPageHeader
        patient={patient}
        patientId={patientId || ''}
        tenant={tenant || ''}
        navigate={navigate}
        activeKey="long-term-frequents"
        disabled={isBusy}
      />

      <div>
        <form id="long-term-frequent-create-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <AppCard
            className="bg-[#fdf5e6]"
            style={{
              boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
            }}
          >
            <span className="text-sm text-blue-600">{NOTE_TEXT}</span>

            <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-x-6 gap-y-4 items-start mt-4">
              {/* ① 施術年月 */}
              <AppFormLabel label="施術年月" required />
              <Controller
                name="visit_yearmonth"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <RA2NDatePicker
                      picker="month"
                      placeholder="年月指定"
                      className="w-52"
                      value={
                        field.value
                          ? convGtJMonth(dayjs(yearmonthToDateStr(Number(field.value))))
                          : ''
                      }
                      onChange={onWarekiMonthChange(field.onChange)}
                      error={!!error}
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ===== A2-specific read-only consent fields ===== */}
              {isA2 && (
                <>
                  {/* ② 傷病名 */}
                  <AppFormLabel label="傷病名" />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {A2_DISEASE_NAME_OPTIONS.map(opt =>
                      opt.value === A2_DISEASE_OTHER_ID ? (
                        <div key={opt.value} className="flex items-center gap-1">
                          <AppCheckbox checked={checkedDiseases.includes(opt.value)} disabled>
                            {opt.label}
                          </AppCheckbox>
                          <span>(</span>
                          <AppTextField value={diseaseNameOther} disabled className="w-48" />
                          <span>)</span>
                        </div>
                      ) : (
                        <AppCheckbox
                          key={opt.value}
                          checked={checkedDiseases.includes(opt.value)}
                          disabled
                        >
                          {opt.label}
                        </AppCheckbox>
                      )
                    )}
                  </div>

                  {/* ③ 施術の種類 */}
                  <AppFormLabel label="施術の種類" />
                  <div>
                    <AppRadioGroup value={treatmentTypeLabel || undefined} disabled>
                      <AppRadio value="はり">はり</AppRadio>
                      <AppRadio value="きゅう">きゅう</AppRadio>
                      <AppRadio value="はり・きゅう併用">はり・きゅう併用</AppRadio>
                    </AppRadioGroup>
                  </div>

                  {/* ④ 初療年月日 */}
                  <AppFormLabel label="初療年月日" />
                  <AppTextField value={firstTreatmentDateDisplay} disabled className="w-52" />

                  {/* ⑤ 施術回数 */}
                  <AppFormLabel label="施術回数" />
                  <div className="flex items-center gap-1">
                    <AppTextField value={String(visitCountDisplay)} disabled className="w-20" />
                    <span>回</span>
                  </div>
                </>
              )}

              {/* ===== R2-specific read-only consent fields ===== */}
              {isR2 && (
                <>
                  {/* 傷病名 — disabled text */}
                  <AppFormLabel label="傷病名" />
                  <AppTextField
                    value={
                      monthData && typeof monthData.disease_name === 'string'
                        ? monthData.disease_name
                        : Array.isArray(monthData?.disease_name)
                          ? monthData.disease_name.join(', ')
                          : ''
                    }
                    disabled
                    className="w-2/5"
                  />

                  {/* 症状 — disabled checkboxes + その他 input */}
                  <AppFormLabel label="症状" />
                  <div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <AppCheckbox
                        checked={
                          !!monthData?.has_consent &&
                          !monthData.symptom_muscle &&
                          !monthData.symptom_joint &&
                          !monthData.symptom_other
                        }
                        disabled
                      >
                        なし
                      </AppCheckbox>
                      <AppCheckbox checked={!!monthData?.symptom_muscle} disabled>
                        筋麻痺
                      </AppCheckbox>
                      <AppCheckbox checked={!!monthData?.symptom_joint} disabled>
                        関節拘縮
                      </AppCheckbox>
                      <div className="flex items-center gap-1">
                        <AppCheckbox checked={!!monthData?.symptom_other} disabled>
                          その他
                        </AppCheckbox>
                        <span>(</span>
                        <AppTextField
                          value={monthData?.symptom_other ?? ''}
                          disabled
                          className="w-48"
                        />
                        <span>)</span>
                      </div>
                    </div>
                  </div>

                  {/* 施術の種類 — disabled checkboxes */}
                  <AppFormLabel label="施術の種類" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <AppCheckbox checked={!!monthData?.massage} disabled>
                      マッサージ
                    </AppCheckbox>
                    <AppCheckbox checked={!!monthData?.orthomorphia} disabled>
                      変形徒手矯正
                    </AppCheckbox>
                  </div>

                  {/* 施術部位 — disabled checkboxes (躯幹, 右上肢, 左上肢, 右下肢, 左下肢) */}
                  <AppFormLabel label="施術部位" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {R2_MASSAGE_PART_OPTIONS.map(opt => (
                      <AppCheckbox
                        key={opt.value}
                        checked={r2SelectedTreatmentPartLabels.has(opt.value)}
                        disabled
                      >
                        {opt.label}
                      </AppCheckbox>
                    ))}
                  </div>

                  {/* 初療年月日 — disabled text */}
                  <AppFormLabel label="初療年月日" />
                  <AppTextField value={firstTreatmentDateDisplay} disabled className="w-52" />

                  {/* 施術回数 — disabled text */}
                  <AppFormLabel label="施術回数" />
                  <div className="flex items-center gap-1">
                    <AppTextField value={String(visitCountDisplay)} disabled className="w-20" />
                    <span>回</span>
                  </div>
                </>
              )}

              {/* ⑥ 患者状態評価日 */}
              <AppFormLabel label="患者状態評価日" required />
              <Controller
                name="valuation_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <RA2NDatePicker
                      placeholder="元号00年00月00日"
                      className="w-52"
                      value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                      onChange={onWarekiDateChange(field.onChange)}
                      error={!!error}
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ⑦ A2: 痛みの強さ */}
              {isA2 && (
                <>
                  <AppFormLabel label="痛みの強さ" required />
                  <Controller
                    name={'degree_of_pain' as any}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <AppForm.Item
                        validateStatus={error ? 'error' : undefined}
                        help={error?.message}
                        className="mb-0"
                      >
                        <AppRadioGroup
                          value={field.value}
                          onChange={e => field.onChange(Number(e.target.value))}
                          disabled={isBusy}
                        >
                          {Array.from({ length: 11 }, (_, i) => i).map(val => (
                            <AppRadio key={val} value={val}>
                              {val}
                            </AppRadio>
                          ))}
                        </AppRadioGroup>
                        <div className="mt-1 text-sm text-blue-600">
                          (注)全く痛みが無い状態を「0」、自分が考え想像しうる最悪の傷みを「10」として今感じている痛みの点数を患者様に聞き、該当の点数をつけてください。
                        </div>
                      </AppForm.Item>
                    )}
                  />
                </>
              )}

              {/* R2: 5 evaluation fields */}
              {isR2 &&
                (
                  [
                    { name: 'valuation_betray_type_id', label: '寝返り' },
                    { name: 'valuation_sit_up_type_id', label: '起き上がり' },
                    { name: 'valuation_sitting_type_id', label: '座位' },
                    { name: 'valuation_stand_up_type_id', label: '立ち上がり' },
                    { name: 'valuation_standing_type_id', label: '立位' },
                  ] as const
                ).map(({ name, label }) => (
                  <React.Fragment key={name}>
                    <AppFormLabel label={label} required />
                    <Controller
                      name={name as any}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <AppForm.Item
                          validateStatus={error ? 'error' : undefined}
                          help={error?.message}
                          className="mb-0"
                        >
                          <AppRadioGroup
                            value={field.value}
                            onChange={e => field.onChange(Number(e.target.value))}
                            disabled={isBusy}
                          >
                            {valuationTypes.map(vt => (
                              <AppRadio key={vt.id} value={vt.id}>
                                {vt.name}
                              </AppRadio>
                            ))}
                          </AppRadioGroup>
                        </AppForm.Item>
                      )}
                    />
                  </React.Fragment>
                ))}

              {/* ⑧ 前月の評価 */}
              <AppFormLabel label="前月の評価" />
              <div>
                <span className={showHasPrev ? 'text-green-700 font-medium' : 'text-gray-500'}>
                  {showHasPrev ? '有り' : '無し'}
                </span>
                {showHasPrev && monthData?.prev_recovery_type_name && (
                  <span className="ml-2 text-gray-600">({monthData.prev_recovery_type_name})</span>
                )}
              </div>

              {/* ⑨ 改善状況 */}
              <AppFormLabel label="改善状況" />
              <Controller
                name="recovery_type_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppRadioGroup
                      value={field.value ?? undefined}
                      onChange={e => field.onChange(Number(e.target.value))}
                      disabled={isBusy || !isPrev}
                    >
                      {recoveryTypes.map(rt => (
                        <AppRadio key={rt.id} value={rt.id}>
                          {rt.name}
                        </AppRadio>
                      ))}
                    </AppRadioGroup>
                  </AppForm.Item>
                )}
              />

              {/* ⑩ 施術が必要な理由 */}
              <AppFormLabel label="施術が必要な理由" required />
              <Controller
                name="treatment_continuation_reason"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppTextArea
                      value={field.value || ''}
                      onChange={e => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      rows={4}
                      maxLength={1000}
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ⑪ はり・きゅう師 / マッサージ師 */}
              <AppFormLabel label={isA2 ? 'はり・きゅう師' : 'マッサージ師'} />
              <Controller
                name="massager_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppSelect
                      className="w-52"
                      value={field.value ?? undefined}
                      onChange={value => {
                        setHasUserSelectedMassager(true);
                        field.onChange(value != null && value !== '' ? Number(value) : null);
                      }}
                      options={massagerOptions}
                      notFoundContent="データがありません"
                      placeholder="選択してください"
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ⑫ 作成日 + 最終施術日取得 */}
              <AppFormLabel label="作成日" required />
              <Controller
                name="create_document_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <div className="flex items-start gap-2">
                      <RA2NDatePicker
                        placeholder="元号00年00月00日"
                        className="w-52"
                        value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                        onChange={onWarekiDateChange(field.onChange)}
                        error={!!error}
                        disabled={isBusy}
                      />
                      <AppButton type="primary" onClick={handleGetLastVisitDate} disabled={isBusy}>
                        最終施術日取得
                      </AppButton>
                    </div>
                  </AppForm.Item>
                )}
              />
            </div>
          </AppCard>
        </form>
      </div>

      <BottomBar
        variant="action"
        visible
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={<div />}
        right={
          <div className="flex gap-4">
            <AppButton
              type="default"
              onClick={handleBack}
              disabled={isBusy}
              className="px-4 py-2 font-medium rounded"
            >
              一覧に戻る
            </AppButton>
            <AppButton
              type="primary"
              htmlType="submit"
              form="long-term-frequent-create-form"
              loading={isBusy}
              className="px-12 py-2 font-medium text-white rounded"
              style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
            >
              <i className="mr-2 fa fa-paper-plane" aria-hidden="true" /> 更新
            </AppButton>
          </div>
        }
      />
    </div>
  );
};

export default LongTermFrequentCreatePage;
